import { clsx, type ClassValue } from "clsx";
import {
  extendTailwindMerge,
  type ConfigExtension,
  type DefaultClassGroupIds,
  type DefaultThemeGroupIds,
} from "tailwind-merge";
import type { FullAutoFill } from "svelte/elements";
import { assetKeyFromUrl, toAssetObjectUrl } from "$lib/media/asset-object-url.js";

// tailwind-merge's built-in `font-size` group only recognises `text-<t-shirt
// size>` (xs/sm/md/lg/xl/2xl/…, matched by a generic regex, not our actual
// `--text-*` scale) or an arbitrary `text-[…]` value. Every OTHER custom
// `--text-*` token in app.css's `@theme` block (micro/caption/thinking/
// input-size/input-size-desktop/chat-*) doesn't match either, so twMerge fell
// through to the `text-color` group instead — which matches ANY word — and a
// later real color class (e.g. `text-online`) silently deleted the size
// (`cn("text-micro", "text-online")` => `"text-online"`, size gone). Extending
// the `font-size` group with our tokens keeps them recognised as sizes, so
// they still collide correctly with each other (and never with a color).
// `text-xs`/`text-sm`/`text-md` already match the built-in t-shirt-size regex
// and don't need listing here (verified against the installed tailwind-merge
// build — see utils.test.ts).
//
// SECOND CEILING (found in review): tailwind-merge declares
// `conflictingClassGroups: { "font-size": ["leading"] }` — a LATER class in
// the `font-size` group deletes an EARLIER `leading-*` class from the merged
// string. That rule models real Tailwind: every built-in font-size utility
// (`text-lg`, our own `thinking`/`chat-body`/`chat-body-mobile`/`chat-title` below) also
// carries a paired `--text-*--line-height`, so a later one legitimately
// overrides an earlier explicit `leading-*`. But `micro`/`caption`/
// `input-size`/`input-size-desktop`/`chat-name`/`chat-edit`/`chat-edit-mobile`
// carry NO line-height pair in app.css (deliberately, see app.css:275) — if
// they inherited that same conflict, a `leading-*` class written BEFORE one of
// them in a `cn()` call got silently deleted for nothing, since the token
// never writes line-height to begin with (verified live:
// AgentComposer.svelte's textarea lost `leading-relaxed` this way, dropping
// its line-height from 1.625 to the inherited body 1.5). Fix: these seven live
// in their OWN classGroup (`font-size-no-leading`) that does NOT list `leading`
// as a conflict, so a `leading-*` class survives regardless of which side of
// the token it's written on — order stops mattering, for these seven only.
// `conflictingClassGroups` below keeps all ten tokens mutually exclusive with
// each other across both groups (verified against the installed
// tailwind-merge build — see utils.test.ts).
// Exported so tailwind-variants (`tv(..., { twMergeConfig })`) merges the same way.
export const twMergeConfig: ConfigExtension<
  "font-size-no-leading" | DefaultClassGroupIds,
  DefaultThemeGroupIds
> = {
  extend: {
    // app.css's sidebar --spacing-* keys. tailwind-merge doesn't read app.css, so
    // without this `h-sidebar-row` is unknown to it and never conflicts with a later
    // `h-8` (both survive, CSS source order picks the winner).
    // Only the sidebar keys: the older ones (tight/inset/…) are left
    // unregistered so their existing cn() call sites keep merging exactly as before.
    theme: {
      spacing: ["sidebar-row", "sidebar-pad", "sidebar-icon", "sidebar-gap", "sidebar-nest"],
    },
    classGroups: {
      // Carry a paired --text-*--line-height in app.css: keep the built-in
      // font-size <-> leading conflict (a later one of these correctly beats
      // an earlier explicit leading-*, same as text-lg/text-sm).
      // `display` (26px, the scale's largest step) is not a t-shirt size, so the
      // built-in regex misses it and a later color class would delete it.
      "font-size": [
        { text: ["thinking", "chat-body", "chat-body-mobile", "chat-title", "display"] },
      ],
      // No line-height pair: never conflicts with leading (see comment above).
      "font-size-no-leading": [
        {
          text: [
            "micro",
            "caption",
            "input-size",
            "input-size-desktop",
            "chat-name",
            "chat-edit",
            "chat-edit-mobile",
          ],
        },
      ],
    },
    conflictingClassGroups: {
      "font-size": ["font-size-no-leading"],
      "font-size-no-leading": ["font-size"],
    },
  },
};
const twMerge = extendTailwindMerge<"font-size-no-leading">(twMergeConfig);

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * `autocomplete` for a field that is NOT the person's own contact details: their
 * display name, an agent's name, a workspace/channel/project/skill name, a
 * roster search box. Left alone the browser answers those with the OS Contacts
 * list — OTHER PEOPLE — which is what covered the Continue button on the "Your
 * name" onboarding step.
 *
 * It is a deliberately UNRECOGNIZED token and NOT `"off"`, which is the whole
 * point of the constant:
 *   • Chrome ignores "on"/"off"/"false" outright for address autofill
 *     (ShouldIgnoreAutocompleteAttribute, autocomplete_parsing_util.h), so
 *     `autocomplete="off"` does not close this dropdown. An unrecognized value
 *     classifies the field as HtmlFieldType::kUnrecognized, which suppresses
 *     suggestions "independently of the predicted type" (autofill_field.h) —
 *     i.e. it beats the id/placeholder/label heuristic that "off" loses to.
 *   • "nope" is in Chromium's own
 *     kNegativeMatchWellIntendedAutocompleteValuesKeywords, so it does not even
 *     raise a DevTools issue.
 *   • Unlike "off" it leaves plain form history (values typed here before)
 *     alone. Only the address book goes away.
 *
 * Known limits, so nobody claims more than this buys:
 *   • Safari collapses "off"/"on"/missing/bogus to the same
 *     AutofillFieldName::None (WebCore Autofill.cpp) and drives its Contacts
 *     button off label/placeholder text instead. This therefore only helps
 *     Safari on the fields that used to DECLARE "name"/"organization"; on the
 *     rest it is a no-op there.
 *   • Chrome's suppression is desktop-only (false under IS_ANDROID/IS_IOS).
 *   • Electron has no address book at all — its autofill agent only ever renders
 *     <datalist> — so this is inert, not harmful, inside the desktop app.
 *   • On a field that genuinely holds the user's own name this gives up WCAG
 *     1.3.5 Identify Input Purpose (AA). Deliberate: the Contacts list covering
 *     the form was the reported bug.
 *
 * The cast is load-bearing. svelte/elements types `autocomplete` as the CLOSED
 * `FullAutoFill` union, so the token cannot be written as an inline literal —
 * svelte-check rejects it and a11y_autocomplete_valid warns on it.
 */
export const NO_CONTACT_AUTOFILL = "nope" as FullAutoFill;

// Deterministic avatar color from a name (workspaces, etc.). Shared so the same
// name resolves to the same swatch everywhere (rail switcher ⇄ profile menu),
// instead of each component keeping its own palette + hash and drifting.
const AVATAR_PALETTE = [
  "#4F46E5",
  "#0891B2",
  "#0D9488",
  "#D97706",
  "#DC2626",
  "#7C3AED",
  "#2563EB",
  "#DB2777",
  "#0D9488",
  "#EA580C",
];

export function nameToColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
}

// THUMBNAILS of the bundled preset portraits (packages/ui/static/agent-avatars/thumb/,
// 192x192, ~12-20KB each) — NOT the 256x256 originals (~10-19KB since CYBORG-1178) the "New agent" wizard's
// Generate button picks from. This array is the FALLBACK every avatarless cybo renders
// at 28-48px on every channel/roster/mention surface, so a full ~600KB original per
// avatarless agent (up to 7 distinct ones in a single workspace) would turn "we don't see
// their avatars" into "now we download megabytes of art nobody asked for" — the thumbnail
// is generated once via `sips -Z 192 -s formatOptions 78`, no runtime resize needed.
// Order matters: presetAvatarForCyboId indexes into this array, so an existing agent's
// assigned portrait must never shift — append new art (also thumbnailed) at the end only,
// never reorder or remove an entry.
export const AGENT_AVATAR_PRESETS = [
  "/agent-avatars/thumb/01-tactical-cartoon.jpeg",
  "/agent-avatars/thumb/02-two-tone-cartoon.jpeg",
  "/agent-avatars/thumb/03-cyborg-sage-in-tech-jacket.jpeg",
  "/agent-avatars/thumb/04-cyborg-sage-with-tech-jacket.jpeg",
  "/agent-avatars/thumb/05-elite-hacker-with-headphones.jpeg",
  "/agent-avatars/thumb/06-female-analyst-with-eye-patch.jpeg",
  "/agent-avatars/thumb/07-female-strategist-arms-crossed.jpeg",
  "/agent-avatars/thumb/08-genius-engineer-in-lab-coat.jpeg",
  "/agent-avatars/thumb/09-hacker-with-headphones-and-hood.jpeg",
  "/agent-avatars/thumb/10-inventor-with-goggles.jpeg",
  "/agent-avatars/thumb/11-samurai-coder-in-armor-focused.jpeg",
  "/agent-avatars/thumb/12-sly-female-analyst-with-eye.jpeg",
] as const;

// Deterministic default portrait for a cybo with NO avatar set: hashes the
// cybo's stable `id` (same char-sum hash idiom as nameToColor, #528) into
// AGENT_AVATAR_PRESETS so the SAME agent always gets the SAME preset portrait
// on every device/render, forever — never random, never re-picked. Most agents
// were otherwise falling back to the identical generic provider-logo tile,
// reading to a human as "we don't see their avatars" even though nothing was
// technically blank. Callers only use this when the real `avatar` is empty —
// an explicitly-set avatar (upload, generate, emoji) always wins.
export function presetAvatarForCyboId(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = id.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AGENT_AVATAR_PRESETS[Math.abs(hash) % AGENT_AVATAR_PRESETS.length];
}

// True when running inside the Electron desktop shell. The shell exposes the
// `window.cyborg7Desktop` bridge (see state/app.svelte.ts); a plain browser
// never has it. Used to skip the web-only "download the desktop app" gate for
// users who are already in the desktop app.
export function isDesktopApp(): boolean {
  return typeof window !== "undefined" && !!window.cyborg7Desktop;
}

// True on macOS / iOS, where the platform "command" key is ⌘ rather than Ctrl.
// Used to render the correct modifier glyph in keyboard hints (⌘K vs Ctrl+K).
// Guards `navigator` so it stays SSR-safe (returns false during prerender).
export function isMac(): boolean {
  if (typeof navigator === "undefined") return false;
  const platform =
    (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ??
    navigator.platform ??
    "";
  return (
    /mac|iphone|ipad|ipod/i.test(platform) || /mac|iphone|ipad|ipod/i.test(navigator.userAgent)
  );
}

// The message-bridge providers whose mirrored speakers are synthetic GUESTS. ONE list:
// every surface that asks "is this person an outsider?" reads the predicate below, so a
// new provider is one edit here instead of a fourth copy of `startsWith("slack:")` — the
// copy that would have shipped a Teams guest with no External badge, a Message button
// opening a dead DM, and a seat on the bill. Mirrors the server's
// MESSAGE_BRIDGE_PROVIDERS (packages/server/src/server/cyborg/message-bridge-providers.ts);
// the UI cannot import server code, so the two lists are kept in step by hand.
const EXTERNAL_GUEST_PROVIDERS = ["slack", "teams"] as const;

/** Which bridged provider a guest speaks from. `as const` above makes this a UNION, not
 *  `string`, and that is the point: ExternalGuestBadge maps it to a mark and a sentence,
 *  so TypeScript is what makes adding a provider to the list above fail loudly there
 *  instead of silently rendering the Slack logo over a Teams customer. */
export type ExternalGuestProvider = (typeof EXTERNAL_GUEST_PROVIDERS)[number];

// The provider a synthetic guest was mirrored in FROM, or null when the id is not a
// guest's. THE ONE derivation: isExternalGuest below is this same function asked a yes/no
// question, so "is an outsider" and "which product is it" can never disagree.
export function externalGuestProvider(userId: string): ExternalGuestProvider | null {
  return EXTERNAL_GUEST_PROVIDERS.find((provider) => userId.startsWith(`${provider}:`)) ?? null;
}

// The bridge a MESSAGE arrived over, by its `source`: a member's bridged message carries their
// own id, so only `source` still says the words came from Slack.
export function bridgeSourceProvider(
  source: string | null | undefined,
): ExternalGuestProvider | null {
  return EXTERNAL_GUEST_PROVIDERS.find((provider) => provider === source) ?? null; // null: typed in Cyborg
}

// True for a synthetic guest user mirrored in from a bridged provider — the yes/no half of
// externalGuestProvider. Those customers are mirrored into a workspace as guests whose id
// is `<provider>:<team>:<user>`; the UI uses this to show an "external" marker, suppress
// affordances that don't apply to them (presence, DMs, a full profile) and keep them out of
// the seat count. Pure id-prefix check — no schema/network dependency. NOT true for a
// `<provider>-bot:` id: that marks a third-party bot's ORIGIN, which the superadmin table
// badges separately.
export function isExternalGuest(userId: string): boolean {
  return externalGuestProvider(userId) !== null;
}

// True for ANY author the bridge mints: a guest above, or a mirrored third-party bot
// (`<provider>-bot:<team>:<bot>`). Not a person — but its name and icon reach the client
// exactly like a guest's (the roster's `external` rows, and the live message), so the
// identity cache takes both.
export function isBridgedAuthor(userId: string): boolean {
  return (
    isExternalGuest(userId) ||
    EXTERNAL_GUEST_PROVIDERS.some((provider) => userId.startsWith(`${provider}-bot:`))
  );
}

// The external-guest provider to BADGE a message author as, or null for "not an outsider".
// A member who posted over a bridge before linking persists as a `slack:` author;
// bridgedMemberLinks (verified-link-gated, server-built) read-repairs that to "via Slack" (#4102).
export function messageGuestProvider(
  fromId: string,
  isAgent: boolean,
  bridgedMemberLinks: ReadonlyMap<string, string>,
): ExternalGuestProvider | null {
  if (isAgent) return null; // null: an agent is never an outsider
  const provider = externalGuestProvider(fromId);
  // Only a bridge-guest id can be read-repaired — a member/uuid id never consults the map.
  if (!provider) return null; // null: not a bridged-guest id
  return bridgedMemberLinks.has(fromId) ? null : provider; // null: a verified-link member
}

// Render a keyboard shortcut with platform-correct modifier glyphs. Pass the
// "logical" combo with a "Mod" placeholder for the command/control key, e.g.
// formatShortcut("Mod+K") → "⌘K" on macOS, "Ctrl+K" elsewhere.
export function formatShortcut(combo: string): string {
  const mac = isMac();
  return combo
    .split("+")
    .map((part) => {
      const key = part.trim();
      switch (key.toLowerCase()) {
        case "mod":
          return mac ? "⌘" : "Ctrl";
        case "shift":
          return mac ? "⇧" : "Shift";
        case "alt":
        case "option":
          return mac ? "⌥" : "Alt";
        case "ctrl":
        case "control":
          return mac ? "⌃" : "Ctrl";
        case "enter":
          return "↵";
        case "esc":
        case "escape":
          return "Esc";
        case "up":
          return "↑";
        case "down":
          return "↓";
        default:
          return key.length === 1 ? key.toUpperCase() : key;
      }
    })
    .join(mac ? "" : "+");
}

export type WithoutChild<T> = T extends { child?: unknown } ? Omit<T, "child"> : T;
export type WithoutChildren<T> = T extends { children?: unknown } ? Omit<T, "children"> : T;
export type WithoutChildrenOrChild<T> = WithoutChildren<WithoutChild<T>>;
export type WithElementRef<T, U extends HTMLElement = HTMLElement> = T & { ref?: U | null };

// Clock time for a timestamp, e.g. "3:04 PM" (24h locales: "15:04"). The single source of truth
// for HH:MM rendering across messages, header rows and the avatar-gutter hover time alike: the
// locale decides 12h/24h and where the day period goes. `locale`/`timeZone` exist for tests.
// The default path is not cached here: V8 caches it and follows a system time-zone change.
export function formatTime(timestamp: number, locale?: string, timeZone?: string): string {
  const opts = { hour: "numeric", minute: "2-digit", timeZone } as const;
  return new Date(timestamp).toLocaleTimeString(locale, opts);
}

// Day divider label: "Today" / "Yesterday" / weekday (this week) / short date.
// Used for the date separators in MessageList and ThreadPanel.
export function formatDate(timestamp: number): string {
  const date = new Date(timestamp);
  const now = new Date();
  // Calendar-day difference — NOT a rolling 24h window. Normalize both to local
  // midnight first: otherwise a message from yesterday afternoon (<24h ago)
  // computes floor(19h/24h)=0 and mislabels as "Today". This drives the Threads
  // feed date labels AND the channel/thread date dividers.
  const midnight = (d: Date): number =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diffDays = Math.round((midnight(now) - midnight(date)) / 86_400_000);

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays > 1 && diffDays < 7) {
    return date.toLocaleDateString(undefined, { weekday: "long" });
  }
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

// Before 2000 = a 0 default or a seconds value: render nothing, never "1970".
function isPlausibleMs(ts: number | null | undefined): ts is number {
  return typeof ts === "number" && Number.isFinite(ts) && ts >= 946_684_800_000;
}

// Time today, "Oct 2" this year, "Oct 2, 2025" older.
export function formatCalendarDate(
  ts: number | null | undefined,
  now: number = Date.now(),
): string {
  if (!isPlausibleMs(ts)) return "";
  const d = new Date(ts);
  const n = new Date(now);
  if (isSameDay(ts, now)) return formatTime(ts);
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(d.getFullYear() === n.getFullYear() ? {} : { year: "numeric" }),
  });
}

export function formatFullDateTime(ts: number | null | undefined): string {
  if (!isPlausibleMs(ts)) return "";
  return new Date(ts).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

// True when both timestamps fall on the same calendar day (local time). Shared
// so date-separator logic doesn't re-derive it via `toDateString()` comparisons.
export function isSameDay(a: number, b: number): boolean {
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
}

// Smart message timestamp for search results: time today, "Yesterday HH:MM",
// else "Mon D HH:MM". Single source for the search-result timestamp that
// MessageSearch and MobileSearchOverlay both rendered identically.
export function formatMessageTimestamp(timestamp: number): string {
  const d = new Date(timestamp);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const msgDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  // Round, not floor: both are local-midnight dates, but a DST transition makes a
  // calendar day 23h/25h, so the raw ratio is 0.958/1.04 — floor would label
  // yesterday as today on a "spring forward" day. Round snaps to the day count.
  const days = Math.round((today.getTime() - msgDate.getTime()) / 86_400_000);
  const time = formatTime(timestamp);
  if (days === 0) return time;
  if (days === 1) return `Yesterday ${time}`;
  return `${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })} ${time}`;
}

// Human-readable byte size, e.g. "820 B" / "3.4 MB" / "2 GB". Whole or ≥10
// values round; otherwise one decimal. Single source for attachment/file sizes
// (ChatMessage, MessageInput, SharedFilesPanel, ChannelFilesList).
export function formatSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return ""; // an attachment with no size shows none, never "NaN KB"
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let v = bytes / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v >= 10 || Number.isInteger(v) ? Math.round(v) : v.toFixed(1)} ${units[i]}`;
}

// Media duration as "m:ss" (e.g. "0:07", "3:42"). Single source for audio/video
// players (VoicePlayer, VideoPlayer, ComposerVoiceRecorder).
export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

// The ONE way to take the first CHARACTER of a person's / organization's /
// workspace's name, for every monogram surface (avatar initials, the Connect org
// mark, project and workspace tiles, member rows).
//
// `name.charAt(0)` and `name[0]` return one UTF-16 CODE UNIT, which is half of a
// surrogate pair: an org named "🚀 Nova" rendered a lone U+D83D — a replacement
// box — in every corner mark on screen. `[...name][0]` fixes that but stops at
// code POINTS, which still cuts a flag (🇲🇽 is two regional indicators), a ZWJ
// family (👨‍👩‍👧) and a decomposed accent ("A" + U+0301) apart — the same
// multi-codepoint shapes resolveAvatarSource below already had to learn about.
//
// `Intl.Segmenter` is the native, dependency-free way to ask for a grapheme, and
// it is the unit a human means by "the first letter". Constructed once: building
// a segmenter is the expensive part, segmenting is not.
const GRAPHEMES = new Intl.Segmenter(undefined, { granularity: "grapheme" });

export function firstGrapheme(value: string): string {
  for (const { segment } of GRAPHEMES.segment(value.trim())) return segment;
  return "";
}

export function getInitials(name: string): string {
  return name.split(" ").map(firstGrapheme).filter(Boolean).slice(0, 2).join("").toUpperCase();
}

// The ONE rule for "what kind of thing is this avatar string?". A cybo avatar
// is either an image URL or a single emoji; everything (no avatar, a plain
// name) otherwise falls back to initials. This used to be re-derived per
// surface with two divergent regexes — the chat-header/CyboSessionAvatar path
// used `/^\p{Emoji}/u` + a URL guard, while the AgentsPane roster used an
// anchored `/^\p{Emoji}$/u`. The anchored one only matches a SINGLE code unit,
// so multi-codepoint emoji (flags like 🇲🇽, ZWJ sequences like 👨‍👩‍👧) were
// rejected and rendered as a broken photo/icon in the roster while the header
// showed the emoji. We standardize on the careful, URL-guarded rule:
//
//   emoji  ⇢ starts with an Emoji code point AND does not start like a URL/path
//            (`http…`, a slug, `:emoji:`, a relative `/path`, a `data:`/`blob:`
//            URI — all begin with `[a-z0-9/:.]`, and some flag/keycap emoji are
//            themselves `\p{Emoji}` digits, so the guard is what disambiguates).
//   image  ⇢ a non-empty avatar that isn't an emoji (treated as a URL).
//   initials ⇢ no avatar at all → initials of `name`.
//
// `\p{Emoji}` (no `$` anchor) deliberately matches the WHOLE emoji including
// multi-codepoint sequences, fixing the roster drift. Pure function — safe to
// unit test and to call from both `.svelte` and `.ts`.
export type AvatarSourceKind = "image" | "emoji" | "initials";
export interface AvatarSource {
  kind: AvatarSourceKind;
  // image → the URL; emoji → the emoji string; initials → the 1–2 letter glyph.
  value: string;
}

function isEmojiAvatar(avatar: string): boolean {
  // The URL guard rejects anything that *starts* like a URL/path/slug/data-URI
  // (`[a-z/:.]`). Bare digits are trickier: a leading `0-9` usually means a
  // path/filename (`0abc.png`, `10.png`) → image, BUT digit keycap emoji
  // (`0️⃣`…`9️⃣`) are an ASCII digit + U+FE0F? + U+20E3 and ARE emoji. So we
  // only treat a leading digit as URL-ish when it is NOT a keycap sequence.
  return /^\p{Emoji}/u.test(avatar) && !/^(?:[a-z/:.]|[0-9](?!️?⃣))/i.test(avatar);
}

export function resolveAvatarSource(avatar: string | null | undefined, name: string): AvatarSource {
  if (avatar) {
    if (isEmojiAvatar(avatar)) return { kind: "emoji", value: avatar };
    return { kind: "image", value: avatar };
  }
  return { kind: "initials", value: getInitials(name || "?") };
}

// Widths the relay thumbnailer (packages/server/.../routes/assets.ts,
// `/api/assets/thumb`) supports — mirror its THUMB_WIDTHS exactly. 360 is the
// FLOOR (smaller than any avatar we render even at DPR 3), so an avatar always
// resolves to the 360px WebP — still a massive win over a multi-MB original
// (one measured agent avatar was 2.02MB vs ~17.5KB processed).
const AVATAR_THUMB_WIDTHS = [360, 720, 1080] as const;

// Our OWN asset bucket — the ONLY host we route through the relay thumbnailer.
// The regex used to live here as a third copy of the same pin; it now comes from
// $lib/media/asset-object-url.ts, which is the ONE definition this and
// render-markdown's inline-image pin share. Anything else (the bundled
// /agent-avatars preset art, `data:` URIs, a self-host fs-backend URL) is left
// untouched so we never send an unresolvable `key` to the thumbnailer.

/**
 * Rewrite an S3 shared-assets avatar URL to the relay's on-demand WebP
 * thumbnail endpoint (`<relayBase>/api/assets/thumb?key=…&w=…`), sized for the
 * rendered avatar × devicePixelRatio and clamped to the endpoint's supported
 * widths. The S3 key is the URL path (the thumbnailer takes a `key`, not a
 * `url`).
 *
 * `relayBase` MUST be the relay's absolute HTTP origin (e.g.
 * https://relay.cyborg7.com), NOT a document-relative path: on desktop the
 * renderer origin is `cyborg://app`, whose protocol handler serves local static
 * files only (no /api → relay proxy), so a relative `/api/assets/thumb` would
 * resolve to the shell (returning index.html), the <img> would fail to decode,
 * and Avatar would fall back to the raw multi-MB original — defeating the
 * optimization AND adding a wasted request. Callers pass `client.httpBase`
 * (runtime-correct for self-host + relay switching) with `CLOUD_RELAY_HTTP_BASE`
 * as the pre-connect fallback.
 *
 * Returns null for any URL we must NOT rewrite (non-shared-assets host,
 * unparseable URL, empty key) — the caller then renders the original URL as-is.
 *
 * `mediaToken` (CYBORG-897) is the short-lived per-user pass the thumbnailer
 * REQUIRES. Optional and appended as `&t=`
 * when present, because an `<img src>` cannot send an Authorization header. A caller
 * with no pass yet still gets a URL, but that URL now 401s rather than serving, so it
 * must re-render once the pass exists (Avatar's reset effect) instead of assuming it
 * painted. Callers get it from
 * `$lib/media/asset-url.svelte.ts` (which dedupes the mint) — never by minting
 * one per image.
 */
export function avatarThumbUrl(
  url: string,
  renderWidth: number,
  relayBase: string,
  mediaToken?: string | null,
): string | null {
  const key = assetKeyFromUrl(url);
  if (!key) return null;
  const dpr = typeof devicePixelRatio === "number" && devicePixelRatio > 0 ? devicePixelRatio : 1;
  const target = renderWidth * dpr;
  const w =
    AVATAR_THUMB_WIDTHS.find((x) => x >= target) ??
    AVATAR_THUMB_WIDTHS[AVATAR_THUMB_WIDTHS.length - 1];
  const base = `${relayBase.replace(/\/+$/, "")}/api/assets/thumb?key=${encodeURIComponent(key)}&w=${w}`;
  return mediaToken ? `${base}&t=${encodeURIComponent(mediaToken)}` : base;
}

// Largest render (x DPR) the bundled 192px preset thumbnails still cover.
const PRESET_THUMB_PX = 192;

/**
 * A cybo whose stored avatar is a FULL bundled portrait (`/agent-avatars/<file>`, what
 * the wizard saves) rendered small gets the `/agent-avatars/thumb/<file>` copy instead:
 * a 16-20px chip was decoding the full art (CYBORG-1195). Only the 12 known presets map;
 * anything else, or a render too large for 192px, returns null.
 */
export function presetAvatarThumbUrl(url: string, renderWidth: number): string | null {
  const match = /^(.*\/agent-avatars\/)([^/?#]+)$/.exec(url);
  if (!match) return null;
  const thumb = `/agent-avatars/thumb/${match[2]}`;
  if (!(AGENT_AVATAR_PRESETS as readonly string[]).includes(thumb)) return null;
  const dpr = typeof devicePixelRatio === "number" && devicePixelRatio > 0 ? devicePixelRatio : 1;
  if (renderWidth * dpr > PRESET_THUMB_PX) return null;
  return `${match[1]}thumb/${match[2]}`;
}

/**
 * The NON-thumb avatar url (CYBORG-897 phase 4) — what Avatar falls back to when
 * the thumbnailer can't serve (503/404) and, on any surface that doesn't
 * thumbnail, what it renders directly.
 *
 * It used to be the raw S3 url, which is the last place in the avatar path still
 * addressing the object on the internet instead of in our app: with the bucket
 * closed it is simply a broken picture, and while the bucket is open it is a link
 * that works forever for anyone who ever saw it. This routes it through the same
 * permanent, authorizing `/api/assets/o/<key>` url everything else now uses.
 *
 * Not ours (preset art, a `data:` uri, someone else's host) ⇒ returned unchanged,
 * exactly like avatarThumbUrl's null.
 *
 * CYBORG-963: the pass goes on ANY of this relay's asset routes, not just the
 * urls the rewrite above had to touch. The old test was `permanent === url`,
 * which conflated "not ours" with "already in a form we don't rewrite" — a
 * self-host `/api/assets/fs/<key>` avatar (mediaAuth-gated, exactly like the
 * object route) came back through the rewrite unchanged and therefore never got
 * the credential. The relay origin is what bounds it: an avatar url is
 * user-settable, so a foreign host whose PATH merely looks like ours must never
 * be handed a credential.
 */
export function avatarImageUrl(url: string, relayBase: string, mediaToken?: string | null): string {
  const permanent = toAssetObjectUrl(url, relayBase);
  if (!mediaToken) return permanent;
  if (!permanent.startsWith(`${relayBase.replace(/\/+$/, "")}/api/assets/`)) return permanent;
  return `${permanent}${permanent.includes("?") ? "&" : "?"}t=${encodeURIComponent(mediaToken)}`;
}

export function debounce<T extends (...args: unknown[]) => void>(fn: T, ms: number): T {
  let timer: ReturnType<typeof setTimeout>;
  return ((...args: unknown[]) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  }) as T;
}

// Leading-edge throttle (Mattermost pattern): fires immediately on the first
// call, then suppresses further calls until `ms` has elapsed since the last
// invocation. Unlike `debounce`, this keeps emitting while the caller is
// active — used for live typing indicators that must refresh before expiry.
export function throttle<T extends (...args: unknown[]) => void>(fn: T, ms: number): T {
  let lastCalled = 0;
  return ((...args: unknown[]) => {
    const now = Date.now();
    if (now - lastCalled >= ms) {
      lastCalled = now;
      fn(...args);
    }
  }) as T;
}

// FLIP duration for the merged Direct Messages list, in ms, from the distance
// the row travels in px.
//
// The sidebar's channel rows use a FIXED 220ms, which was tuned when a DM row
// could only ever move within its own short per-kind block. In ONE merged list
// a row can cross the whole section, and a fixed duration means velocity scales
// with distance — a twenty-row jump covers 640px in the same 220ms a one-row hop
// covers 32px. Svelte's own default (sqrt(d) * 120) overcorrects the other way:
// it is tuned for card-sized lists and puts a one-row hop at ~679ms.
//
// So: a mild distance term on top of the established 220ms base, hard-capped so
// the longest possible jump still lands before the next glance.
//   0px  →   0ms  ·  32px (one row) → 226ms  ·  640px (twenty rows) → 348ms
//
// The `d < 1 → 0` is load-bearing, not input padding. Svelte's flip has no
// zero-delta short-circuit: it computes dx/dy and then calls duration(...)
// regardless. With a plain numeric duration, EVERY row in the block would get a
// translate(0, 0) animation on every reconcile — including the muted rows the
// comparator deliberately holds in place, and every row on every unrelated
// re-render. Returning 0 means an unmoved row gets no transform and no
// compositing layer.
export function dmFlipDuration(d: number): number {
  if (!Number.isFinite(d) || d < 1) return 0;
  return Math.min(420, 220 + d * 0.2);
}
